import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";

// 's' is float representing a time in seconds.
function makeTimeString(s) {
    let min = Math.floor(s / 60);
    let sec = padSmallInt(Math.floor(s % 60));
    return `${min || '0'}:${sec || '00'}`;
}

// Padding function. 'n' is an integer.
function padSmallInt(n) {
    return n < 10 ? "0" + n : n;
}

function placeCursor(e, progBarRef, setLeftShift, propDone, setPropDone) {
    // Calculate x position of the cursor (from the bounding rect left edge)
    let touchX = e.clientX;
    let progX = progBarRef.current.getBoundingClientRect().left;
    let x = touchX - progX;

    // Width of the progress bar area
    let w = progBarRef.current.clientWidth;

    // Keep the cursor in the progress bar area
    if (x < 0) {
        x = 0;
    } else if (x > w) {
        x = w;
    }

    // Set prop done and left shift.
    setPropDone(x / w);
    setLeftShift(`${propDone * 100 - 1}%`);
}

function setVideoTime(videoRef, propDone, videoDuration) {
    let currTime = propDone * videoDuration;
    if (currTime < videoDuration) {
        videoRef.current.currentTime = currTime;
    }
}

function videoProgressHandler(
    videoRef,
    videoDuration,
    setVideoDuration,
    setVideoDurationString,
    setVideoCurrentTime,
    allowSlide,
    progBarRef,
    propDone,
    setPropDone
) {
    // Set the video duration if not already done
    if (!videoDuration) {
        setVideoDuration(videoRef.current.duration);
        setVideoDurationString(makeTimeString(videoRef.current.duration));
    }

    // Set the current time
    if (videoRef.current.currentTime) setVideoCurrentTime(videoRef.current.currentTime);

    // Calculate the proportion completed. Only set the state if the user is not moving the cursor.
    let pDone = videoRef.current.currentTime / videoRef.current.duration;

    if (!allowSlide) {
        progBarRef.current.value = pDone;
        setPropDone(pDone);
    } else {
        setVideoTime(videoRef, propDone, videoDuration);
    }
}

function VideoPlayerControls(props) {
    let [videoDuration, setVideoDuration] = useState(
        props.videoRef.current.duration
    );
    let [videoDurationString, setVideoDurationString] = useState("");
    let [videoProgString, setVideoProgString] = useState("");
    let [videoCurrentTime, setVideoCurrentTime] = useState(0);
    let [propDone, setPropDone] = useState(0);
    let [leftShift, setLeftShift] = useState(0);
    let [allowSlide, setAllowSlide] = useState(false);

    let cursorRef = useRef();
    let progBarRef = useRef();

    useEffect(() => {
        const videoTimer = setInterval(() => {
            videoProgressHandler(
                props.videoRef,
                videoDuration,
                setVideoDuration,
                setVideoDurationString,
                setVideoCurrentTime,
                allowSlide,
                progBarRef,
                propDone,
                setPropDone,
            );
        }, 50);
        return () => clearInterval(videoTimer);
    });

    useEffect(() => {
        //Get the video duration after the metadata has loaded.
        props.videoRef.current.onloadedmetadata = (e) => {
            setVideoDuration(e.target.duration);
        };
    });

    useEffect(() => {
        setVideoDurationString(makeTimeString(videoDuration));
    }, [videoDuration]);

    useEffect(() => {
        setVideoProgString(makeTimeString(videoCurrentTime));
    }, [videoCurrentTime]);

    useEffect(() => {
        setLeftShift(`${propDone * 100 - 1}%`);
    }, [propDone]);

    return (
        <Box
            sx={{
                width: "max(750px, 100%)",
                marginTop: "2%",
                display: "flex",
                flexDirection: "row",
                justifyContent: "start",
                alignItems: "center",
                columnGap: "max(2px, 1%)"
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    width: "5.7%"
                }}
            >
                <Typography
                    sx={{
                        fontSize: "max(12px, 1.3889vh)",
                        lineHeight: "max(13px, 1.6204vh)",
                        letterSpacing: "1px",
                        color: "text.contrast",
                        textAlign: "left",
                        width: "43%",
                    }}
                >
                    {videoProgString}
                </Typography>
                <Typography
                    sx={{
                        fontSize: "max(12px, 1.3889vh)",
                        lineHeight: "max(13px, 1.6204vh)",
                        letterSpacing: "1px",
                        color: "text.contrast",
                        textAlign: "center",
                        width: "13%"

                    }}
                >
                    /
                </Typography>
                <Typography
                    sx={{
                        fontSize: "max(12px, 1.3889vh)",
                        lineHeight: "max(13px, 1.6204vh)",
                        letterSpacing: "2px",
                        color: "text.contrast",
                        textAlign: "right",
                        width: "44%"

                    }}
                >
                    {videoDurationString} 
                </Typography>
            
            </Box>
            <Box
                ref={progBarRef}
                onPointerMove={(e) => {
                    if (allowSlide) {
                        placeCursor(
                            e,
                            progBarRef,
                            setLeftShift,
                            propDone,
                            setPropDone,
                            props.videoRef,
                            videoDuration
                        );
                    }
                }}
                onPointerDown={(e) => {
                    setAllowSlide(true);
                    if (!props.videoRef.current.paused) {
                        props.videoRef.current.pause();
                    }
                    placeCursor(
                        e,
                        progBarRef,
                        setLeftShift,
                        propDone,
                        setPropDone,
                        props.videoRef,
                        videoDuration
                    );
                }}
                onPointerUp={(e) => {
                    setAllowSlide(false);
                    if (props.videoRef.current.paused) {
                        props.videoRef.current.play();
                    }
                }}
                sx={{
                    width: "94%",
                    touchAction: "none",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    position: "relative"
                }}
            >
                <progress
                    value={`${propDone}`}
                    min="0"
                    style={{
                        width: "100%",
                        height: "0.463vh",
                        background: "#ffffff",
                    }}
                ></progress>
                <Box
                    ref={cursorRef}
                    sx={{
                        backgroundColor: "primary.main",
                        boxShadow: "0px 0px .9259vh #0000004d",
                        height: "1.8519vh",
                        aspectRatio: "1",
                        borderRadius: ".9259vh",
                        position: "absolute",
                        left: leftShift
                    }}
                ></Box>
            </Box>
        </Box>
    );
}

export default VideoPlayerControls;
